import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import icArrowLeft from "@/assets/icons/ic-arrow-left.svg";
import { CardNews, type CardNewsData } from "@/components/ax-news/card-news";
import { Pagination } from "@/components/common";
import { AX_NEWS_SECTIONS, type AxNewsSectionKey } from "@/types/ax-news";
import {
  AXNewsService,
  type ICategory,
  type IGetListArticlesRes,
} from "@/services/ax-news-service";

const SECTION_KEY_TO_CATEGORY: Record<AxNewsSectionKey, ICategory> = {
  "use-case": "ai_use_case",
  trend: "ax_trend",
  "pwc-solutions": "pwc_digital_solutions",
};

const mapToCardNewsData = (article: IGetListArticlesRes): CardNewsData => ({
  id: article.id,
  image: article.cover_image_url,
  title: article.title,
  description: article.content,
  date: article.created_at,
  href: `/ax-news/list/${article.id}`,
});

const AX_NEWS_PAGE_SIZE = 9;

function isValidSection(value: string | null): value is AxNewsSectionKey {
  return value === "use-case" || value === "trend" || value === "pwc-solutions";
}

export const AXListPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sectionParam = searchParams.get("section");

  // Invalid or missing section → bounce back to the AX News home.
  useEffect(() => {
    if (!isValidSection(sectionParam)) {
      navigate("/ax-news", { replace: true });
    }
  }, [sectionParam, navigate]);

  const section: AxNewsSectionKey | null = isValidSection(sectionParam) ? sectionParam : null;
  const title = section ? AX_NEWS_SECTIONS[section].title : "";

  const [items, setItems] = useState<CardNewsData[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  // Section change → reset to page 1.
  useEffect(() => {
    setCurrentPage(1);
  }, [section]);

  const fetchList = useCallback(async () => {
    if (!section) return;
    setIsLoading(true);
    try {
      const res = await AXNewsService.getListByArticles({
        category: SECTION_KEY_TO_CATEGORY[section],
        page: currentPage,
        page_size: AX_NEWS_PAGE_SIZE,
      });
      const data = res.data.data;
      const items: CardNewsData[] = (data?.items ?? []).map(mapToCardNewsData);
      setItems(items);
      setTotalPages(data?.total_pages ?? 1);
    } catch {
      setItems([]);
      setTotalPages(1);
    } finally {
      setIsLoading(false);
    }
  }, [section, currentPage]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  // Clamp current page if total shrinks (e.g. section switch with fewer items).
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [currentPage, totalPages]);

  const grid = useMemo(() => {
    if (items.length === 0) {
      return (
        <div className="flex flex-col items-center gap-3 py-20 text-content-medium">
          <p className="text-h3">No news yet</p>
          <p className="text-body">Check back later for updates.</p>
        </div>
      );
    }
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 ">
        {items.map((item) => (
          <CardNews key={item.id} data={{ ...item, href: `/ax-news/list/${item.id}` }} />
        ))}
      </div>
    );
  }, [items]);

  // Render nothing while we're still deciding whether the section is valid
  // (avoids a flash of empty state before the redirect fires).
  if (!section) return null;

  return (
    <>
      {isLoading && (
        <div className="flex items-center justify-center py-20 h-[60vh] w-full">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-black/10 border-t-[#FF3B00]" />
        </div>
      )}
      <div 
        className="flex flex-col gap-6 lg:gap-12 px-5 py-6 lg:px-[100px] lg:py-10 max-w-[1440px] mx-auto w-full"
        style={{ display: isLoading ? "none" : "flex" }}
      >
      {/* header: mobile=column, desktop=row */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:gap-[2px]">
        <button
          type="button"
          onClick={() => navigate(-1)}
          aria-label="뒤로 가기"
          className="w-12 h-12 flex items-center justify-center"
        >
          <img src={icArrowLeft} alt="Icon Arrow Left" />
        </button>
        <div className="flex flex-col gap-3 lg:gap-[12px]">
          <h1 className="text-[32px] leading-[40px] lg:text-[32px] lg:leading-10 text-content-strong font-bold tracking-[-0.64px]">
            {title}
          </h1>
          {/* <p className="text-body text-content-medium lg:hidden">
            최신 AI 트렌드와 비즈니스 인사이트를 확인하세요
          </p> */}
        </div>
      </div>

      {grid}

      <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
    </div>
    </>
  );
};

export default AXListPage;
